import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAppState, Project } from '../../contexts/AppContext';

interface ProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    editProject?: Project | null;
}

const colorOptions = [
    { color: '#2A7A85', bgColor: '#D9EEF2', name: 'Teal' },
    { color: '#7B61C4', bgColor: '#EDE9F7', name: 'Purple' },
    { color: '#3DA58A', bgColor: '#D5F2EA', name: 'Green' },
    { color: '#E8A547', bgColor: '#FFF4E5', name: 'Orange' },
    { color: '#D66B5C', bgColor: '#FFEAE7', name: 'Red' },
    { color: '#5E6AD2', bgColor: '#E8E9F8', name: 'Blue' },
];

const iconOptions = ['📁', '💼', '🏠', '💪', '📚', '🎯', '🚀', '💡', '🎨', '📱', '🛒', '✈️'];

export default function ProjectModal({ isOpen, onClose, editProject }: ProjectModalProps) {
    const { addProject, updateProject } = useAppState();
    const [name, setName] = useState('');
    const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
    const [selectedIcon, setSelectedIcon] = useState('📁');

    // Load edit data
    useEffect(() => {
        if (editProject) {
            setName(editProject.name);
            const color = colorOptions.find((c) => c.color === editProject.color) || colorOptions[0];
            setSelectedColor(color);
            setSelectedIcon(editProject.icon || '📁');
        } else {
            setName('');
            setSelectedColor(colorOptions[0]);
            setSelectedIcon('📁');
        }
    }, [editProject, isOpen]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        const projectData: Omit<Project, 'id'> = {
            name: name.trim(),
            color: selectedColor.color,
            bgColor: selectedColor.bgColor,
            icon: selectedIcon,
            status: 'active',
        };

        if (editProject) {
            updateProject(editProject.id, projectData);
        } else {
            addProject(projectData);
        }

        setName('');
        setSelectedColor(colorOptions[0]);
        setSelectedIcon('📁');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 pb-8 max-h-[90vh] overflow-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-[#1A2832]">
                        {editProject ? 'Edit Project' : 'New Project'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]"
                    >
                        <X className="w-5 h-5 text-[#7B8C9B]" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] mb-2">
                            Project Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Work, Personal, Side Project"
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832]"
                            autoFocus
                        />
                    </div>

                    {/* Icon */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] mb-2">
                            Icon
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {iconOptions.map((icon) => (
                                <button
                                    key={icon}
                                    type="button"
                                    onClick={() => setSelectedIcon(icon)}
                                    className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition ${selectedIcon === icon
                                            ? 'bg-[#0D5F6B] ring-2 ring-[#0D5F6B] ring-offset-2'
                                            : 'bg-[#F5F8FA] hover:bg-[#EEF3F6]'
                                        }`}
                                >
                                    {icon}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Color */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] mb-2">
                            Color
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {colorOptions.map((option) => (
                                <button
                                    key={option.color}
                                    type="button"
                                    onClick={() => setSelectedColor(option)}
                                    className={`w-10 h-10 rounded-lg transition ${selectedColor.color === option.color
                                            ? 'ring-2 ring-offset-2 ring-[#0D5F6B]'
                                            : ''
                                        }`}
                                    style={{ backgroundColor: option.color }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Preview */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] mb-2">
                            Preview
                        </label>
                        <div
                            className="flex items-center gap-3 px-4 py-3 rounded-xl"
                            style={{ backgroundColor: selectedColor.bgColor }}
                        >
                            <span className="text-xl">{selectedIcon}</span>
                            <span
                                className="font-semibold"
                                style={{ color: selectedColor.color }}
                            >
                                {name || 'Project Name'}
                            </span>
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={!name.trim()}
                        className="w-full py-4 rounded-xl bg-[#0D5F6B] text-white text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)]"
                    >
                        {editProject ? 'Save Changes' : 'Create Project'}
                    </button>
                </form>
            </div>
        </div>
    );
}
